import React, { useState } from 'react';
import { useCollegePrepContext } from '../../../contexts/CollegePrepContext';

const ActionTracker = () => {
  const { timelineActions, addTimelineAction, updateTimelineAction, deleteTimelineAction } = useCollegePrepContext();
  const [showAddForm, setShowAddForm] = useState(false);
  const [newAction, setNewAction] = useState({
    description: '',
    phase: 'research',
    priority: 'medium',
    dueDate: '',
    relatedPrograms: []
  });

  const phases = {
    research: { label: 'Research & Discovery', color: 'emerald' },
    prep: { label: 'Application Prep', color: 'azure' },
    applications: { label: 'Application Season', color: 'violet' },
    decisions: { label: 'Decision Time', color: 'emerald' }
  };

  const priorityColors = {
    high: 'bg-red-100 text-red-700 border-red-200',
    medium: 'bg-amber-100 text-amber-700 border-amber-200',
    low: 'bg-emerald-100 text-emerald-700 border-emerald-200'
  };

  const handleAddAction = (e) => {
    e.preventDefault();
    if (newAction.description.trim()) {
      addTimelineAction({
        ...newAction,
        id: Date.now().toString(),
        completed: false,
        dateAdded: new Date().toISOString()
      });
      setNewAction({
        description: '',
        phase: 'research',
        priority: 'medium',
        dueDate: '',
        relatedPrograms: []
      });
      setShowAddForm(false);
    }
  };

  const handleToggleComplete = (actionId) => {
    const action = timelineActions.find(a => a.id === actionId);
    if (action) {
      updateTimelineAction(actionId, {
        completed: !action.completed,
        completedAt: !action.completed ? new Date().toISOString() : null
      });
    }
  };

  const getActionsByPhase = (phase) => {
    return timelineActions.filter(action => action.phase === phase);
  };

  const getOverallProgress = () => {
    if (timelineActions.length === 0) return 0;
    const completed = timelineActions.filter(a => a.completed).length;
    return Math.round((completed / timelineActions.length) * 100);
  };

  const getUpcomingActions = () => {
    const now = new Date();
    return timelineActions
      .filter(action => {
        if (action.completed) return false;
        if (!action.dueDate) return true; // No due date = always upcoming
        const dueDate = new Date(action.dueDate);
        const daysDiff = Math.ceil((dueDate - now) / (1000 * 60 * 60 * 24));
        return daysDiff <= 14; // Due within 2 weeks
      })
      .sort((a, b) => {
        // Sort by priority and due date
        const priorityOrder = { high: 3, medium: 2, low: 1 };
        if (priorityOrder[a.priority] !== priorityOrder[b.priority]) {
          return priorityOrder[b.priority] - priorityOrder[a.priority];
        }
        if (a.dueDate && b.dueDate) {
          return new Date(a.dueDate) - new Date(b.dueDate);
        }
        return 0;
      });
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const getDaysUntilDue = (dueDateString) => {
    if (!dueDateString) return null;
    const now = new Date();
    const dueDate = new Date(dueDateString);
    const daysDiff = Math.ceil((dueDate - now) / (1000 * 60 * 60 * 24));
    return daysDiff;
  };

  return (
    <div className="space-y-6">
      {/* Overview */}
      <div className="bg-gradient-to-r from-azure-50 to-indigo-50 border border-azure-200 rounded-xl p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-azure-800">Action Tracker</h3>
          <div className="text-right">
            <div className="text-2xl font-bold text-azure-700">{getOverallProgress()}%</div>
            <div className="text-sm text-azure-600">Overall Progress</div>
          </div>
        </div>
        
        <div className="w-full bg-azure-200 rounded-full h-3 mb-4">
          <div 
            className="bg-azure-500 h-3 rounded-full transition-all duration-300"
            style={{ width: `${getOverallProgress()}%` }}
          />
        </div>
        
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
          {Object.entries(phases).map(([key, phase]) => {
            const phaseActions = getActionsByPhase(key);
            const completedCount = phaseActions.filter(a => a.completed).length;
            
            return (
              <div key={key} className="text-center">
                <div className="font-medium text-azure-700">{completedCount}/{phaseActions.length}</div>
                <div className="text-azure-600">{phase.label}</div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Upcoming Actions */}
      <div className="bg-white border border-stone-200 rounded-xl p-6">
        <div className="flex items-center justify-between mb-4">
          <h4 className="font-semibold text-slate-900">🎯 Upcoming Actions</h4>
          <button
            onClick={() => setShowAddForm(true)}
            className="px-3 py-1 bg-azure-600 text-white text-sm font-medium rounded hover:bg-azure-700"
          >
            + Add Action
          </button>
        </div>
        
        {getUpcomingActions().length === 0 ? (
          <div className="text-center py-6 text-stone-500">
            <div className="text-3xl mb-2">✅</div>
            <div className="font-medium">No upcoming actions</div>
            <div className="text-sm">All current tasks are complete!</div>
          </div>
        ) : (
          <div className="space-y-3">
            {getUpcomingActions().slice(0, 5).map((action) => {
              const daysUntilDue = getDaysUntilDue(action.dueDate);
              const isOverdue = daysUntilDue !== null && daysUntilDue < 0;
              const isUrgent = daysUntilDue !== null && daysUntilDue <= 3 && daysUntilDue >= 0;
              
              return (
                <div 
                  key={action.id}
                  className={`flex items-center gap-3 p-3 rounded-lg border ${
                    isOverdue ? 'bg-red-50 border-red-200' :
                    isUrgent ? 'bg-amber-50 border-amber-200' :
                    'bg-stone-50 border-stone-200'
                  }`}
                >
                  <button
                    onClick={() => handleToggleComplete(action.id)}
                    className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-all ${
                      action.completed
                        ? 'bg-emerald-500 border-emerald-500 text-white'
                        : 'border-stone-300 hover:border-emerald-300'
                    }`}
                  >
                    {action.completed && '✓'}
                  </button>
                  
                  <div className="flex-1">
                    <div className={`font-medium text-sm ${
                      action.completed ? 'line-through text-stone-500' : 'text-slate-900'
                    }`}>
                      {action.description}
                    </div>
                    
                    <div className="flex items-center gap-2 mt-1">
                      <span className={`px-2 py-1 text-xs rounded-full border ${priorityColors[action.priority]}`}>
                        {action.priority}
                      </span>
                      
                      <span className={`text-xs px-2 py-1 rounded-full ${
                        phases[action.phase] ? `bg-${phases[action.phase].color}-100 text-${phases[action.phase].color}-700` : 'bg-stone-100 text-stone-700'
                      }`}>
                        {phases[action.phase]?.label || action.phase}
                      </span>
                      
                      {action.dueDate && (
                        <span className={`text-xs ${
                          isOverdue ? 'text-red-600 font-medium' :
                          isUrgent ? 'text-amber-600 font-medium' :
                          'text-stone-500'
                        }`}>
                          {isOverdue ? `${Math.abs(daysUntilDue)} days overdue` :
                           daysUntilDue === 0 ? 'Due today' :
                           daysUntilDue === 1 ? 'Due tomorrow' :
                           `Due ${formatDate(action.dueDate)}`}
                        </span>
                      )}
                    </div>
                  </div>
                  
                  <button
                    onClick={() => deleteTimelineAction(action.id)}
                    className="text-stone-400 hover:text-red-500 transition-colors p-1"
                  >
                    ×
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Add Action Form */}
      {showAddForm && (
        <div className="bg-white border border-stone-200 rounded-xl p-6">
          <h4 className="font-semibold text-slate-900 mb-4">Add New Action</h4>
          
          <form onSubmit={handleAddAction} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Action Description
              </label>
              <input
                type="text"
                value={newAction.description}
                onChange={(e) => setNewAction({ ...newAction, description: e.target.value })}
                className="w-full px-3 py-2 border border-stone-300 rounded-lg focus:ring-2 focus:ring-azure-500 focus:border-azure-500"
                placeholder="What needs to be done?"
                required
              />
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Phase
                </label>
                <select
                  value={newAction.phase}
                  onChange={(e) => setNewAction({ ...newAction, phase: e.target.value })}
                  className="w-full px-3 py-2 border border-stone-300 rounded-lg focus:ring-2 focus:ring-azure-500 focus:border-azure-500"
                >
                  {Object.entries(phases).map(([key, phase]) => (
                    <option key={key} value={key}>{phase.label}</option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Priority
                </label>
                <select
                  value={newAction.priority}
                  onChange={(e) => setNewAction({ ...newAction, priority: e.target.value })}
                  className="w-full px-3 py-2 border border-stone-300 rounded-lg focus:ring-2 focus:ring-azure-500 focus:border-azure-500"
                >
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Due Date (Optional)
                </label>
                <input
                  type="date"
                  value={newAction.dueDate}
                  onChange={(e) => setNewAction({ ...newAction, dueDate: e.target.value })}
                  className="w-full px-3 py-2 border border-stone-300 rounded-lg focus:ring-2 focus:ring-azure-500 focus:border-azure-500"
                />
              </div>
            </div>
            
            <div className="flex gap-2">
              <button
                type="submit"
                className="px-4 py-2 bg-azure-600 text-white font-medium rounded-lg hover:bg-azure-700"
              >
                Add Action
              </button>
              <button
                type="button"
                onClick={() => setShowAddForm(false)}
                className="px-4 py-2 border border-stone-300 text-stone-700 font-medium rounded-lg hover:bg-stone-50"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

export default ActionTracker;